"use client";

import type { ReactNode } from "react";
import { Controller, useFormContext } from "react-hook-form";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { Theme } from "@mui/material/styles";
import { containedCardSx } from "./containedCardSx";
import { Fade } from "@mui/material";

export type FormCheckboxProps = {
  name: string;
  label?: ReactNode;
  helperText?: ReactNode;
  disabled?: boolean;
  /**
   * Wrap the checkbox row in a bordered card. Border turns brand-blue when
   * checked and error-red when invalid. Children, if passed, render inside
   * the card below the checkbox row. Helper text always renders outside
   * the card.
   */
  contained?: boolean;
  /**
   * Which side of the checkbox the label sits on. Defaults to `"end"`
   * (checkbox first, label after).
   */
  labelPlacement?: "start" | "end";
  /**
   * Horizontal alignment of the label and checkbox within the row.
   * `"space-between"` pushes the label and checkbox to opposite edges of a
   * full-width row. `"end"` right-aligns both. Both stretch the row to
   * 100% width.
   */
  labelAlign?: "start" | "end" | "space-between";
  /**
   * Optional content rendered below the checkbox when it's checked. When
   * `contained`, children render inside the card; otherwise they render
   * as a sibling below the row. Children unmount when unchecked so nested
   * inputs don't validate against hidden state. Children handle their own
   * internal styling — `FormCheckbox` only positions them.
   */
  children?: ReactNode;
};

const baseRowStyle = (theme: Theme) => ({
  width: "100%",
  marginLeft: 0,
  alignItems: "center",
  gap: theme.spacing(2),
});

export const FormCheckbox = ({
  name,
  label,
  helperText,
  disabled,
  contained,
  labelPlacement = "end",
  labelAlign = "start",
  children,
}: FormCheckboxProps) => {
  const { control } = useFormContext();
  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { ref, ...field }, fieldState }) => {
        const hasError = !!fieldState.error;
        const checked = !!field.value;

        const controlLabel = (
          <FormControlLabel
            disabled={disabled}
            labelPlacement={labelPlacement}
            sx={(theme: Theme) => ({
              ...baseRowStyle(theme),
              ...theme.typography.bodyMdMedium,
              ...(labelAlign === "space-between"
                ? { justifyContent: "space-between" }
                : {}),
              ...(labelAlign === "end" ? { justifyContent: "flex-end" } : {}),
              ...(labelAlign === "start"
                ? { justifyContent: "flex-start" }
                : {}),
            })}
            control={
              <Checkbox
                {...field}
                slotProps={{ input: { ref } }}
                id={name}
                checked={checked}
                color={hasError ? "error" : "primary"}
              />
            }
            label={label}
            // Drive the FormControlLabel into Mui-error so our theme paints
            // the label text red. The class is set via slotProps because
            // `<FormControlLabel error>` isn't part of the public prop type.
            slotProps={{
              typography: hasError ? { className: "Mui-error" } : {},
            }}
          />
        );

        const helperLine = fieldState.error?.message ?? helperText;

        const expandedChildren =
          checked && children ? (
            // The Box gives Fade a real DOM node to hold its ref to. Fade
            // clones its child and reads `node.scrollTop` (reflow) during the
            // enter transition; RHF field children like FormToggleButtonGroup
            // don't forward refs, so handing them to Fade directly resolves the
            // ref to null and throws "Cannot read properties of null".
            <Fade in={checked}>
              <Box sx={{ marginTop: 4 }}>{children}</Box>
            </Fade>
          ) : null;

        return (
          <Stack
            spacing={0}
            sx={(theme: Theme) => ({
              ...(contained ? containedCardSx(checked, hasError)(theme) : {}),
              alignItems: "stretch",
            })}
          >
            {controlLabel}
            {(expandedChildren || helperLine) && (
              <Stack spacing={0.5} sx={{ paddingLeft: 8 }}>
                {expandedChildren}
                {helperLine && (
                  <Typography
                    variant="caption"
                    color={hasError ? "error" : "text.secondary"}
                    sx={{ marginTop: 0.5 }}
                  >
                    {helperLine}
                  </Typography>
                )}
              </Stack>
            )}
          </Stack>
        );
      }}
    />
  );
};
